import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Smart-cart recommendation engine for The Lure Pack.
 *
 * Renders nothing of its own: cards are server-rendered by Shopify through the
 * Product Recommendations API + Section Rendering API (section lp-cart-recs),
 * so price, currency, availability and discount data always come from Shopify
 * for the visitor's current market. This module only decides which seeds to
 * ask about, merges/dedupes the returned cards, and performs quick add through
 * the theme's documented cart event contract.
 *
 * Failure policy: fail closed. Any error leaves the region hidden and the
 * native cart (quantity, removal, checkout) untouched.
 */

const SEED_LIMIT = 3;
const CACHE_TTL_MS = 5 * 60 * 1000;
const CACHE_MAX_ENTRIES = 24;
const SECTIONS_PARAM_MAX = 5;
const GEAR_SLOTS = ['rod', 'reel', 'line'];

/**
 * 2026-09-14 owner pass ("the smart cart lazy loads, make it show straight
 * away"): the recommendation HTML cache is module-level, shared by every
 * <lp-cart-recs> on the page and by the idle PREWARM below, so a drawer
 * open normally renders from memory instead of waiting on two or three
 * network round trips. In-flight requests are deduped by URL.
 */
const recsCache = new Map();
const recsInflight = new Map();

function withPreviewParam(url) {
  try {
    const preview = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (!preview) return url;
    return `${url}${url.includes('?') ? '&' : '?'}preview_theme_id=${encodeURIComponent(preview)}`;
  } catch {
    return url;
  }
}

function recsUrlFor(base, section, productId, intent) {
  return `${base || '/recommendations/products'}?section_id=${encodeURIComponent(section || 'lp-cart-recs')}&product_id=${productId}&intent=${intent}&limit=8`;
}

/**
 * Loads (or serves from cache) the server-rendered card HTML for a URL.
 * Resolves to null on a non-OK response, rejects on network failure.
 * @returns {Promise<string | null>}
 */
async function loadRecsHtml(url) {
  const cached = recsCache.get(url);
  if (cached && Date.now() - cached.at < CACHE_TTL_MS) return cached.html;
  if (recsInflight.has(url)) return recsInflight.get(url);
  const job = (async () => {
    let response = await fetch(withPreviewParam(url), { headers: { Accept: 'text/html' } });
    // One bounded retry on throttle/transient server errors: the empty-cart
    // path has no later cart event to wake it up, so a single 429 would
    // otherwise blank the region for the whole session.
    if (response.status === 429 || response.status === 503) {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      response = await fetch(withPreviewParam(url), { headers: { Accept: 'text/html' } });
    }
    if (!response.ok) return null;
    const html = await response.text();
    recsCache.set(url, { html, at: Date.now() });
    while (recsCache.size > CACHE_MAX_ENTRIES) {
      recsCache.delete(recsCache.keys().next().value);
    }
    return html;
  })().finally(() => recsInflight.delete(url));
  recsInflight.set(url, job);
  return job;
}

/**
 * Idle prewarm: fetch the complementary cards for the seeds the drawer will
 * ask about — the items already in the cart (server-rendered onto the shell)
 * plus, on a product page, the product the visitor is about to add. Runs
 * once per page, after the page is idle, capped at SEED_LIMIT + 1 requests,
 * and never touches the DOM; a failure here just means the drawer fetches
 * on open as before.
 */
function prewarmRecs() {
  try {
    const shell = document.querySelector('lp-cart-recs');
    if (!shell) return;
    const base = shell.dataset.recsUrl;
    const section = shell.dataset.sourceSection;
    const seeds = [];
    const pdpId =
      Number(window.ShopifyAnalytics?.meta?.product?.id) ||
      Number(document.querySelector('product-form-component input[name="product-id"]')?.value);
    if (pdpId) seeds.push(pdpId);
    (shell.dataset.cartIds || '')
      .split(',')
      .map((entry) => Number(entry.trim()))
      .filter(Boolean)
      .slice(0, SEED_LIMIT)
      .forEach((id) => {
        if (!seeds.includes(id)) seeds.push(id);
      });
    if (seeds.length === 0) return;
    const run = () => {
      seeds.slice(0, SEED_LIMIT + 1).forEach((id) => {
        loadRecsHtml(recsUrlFor(base, section, id, 'complementary')).catch(() => {});
      });
      // The product being viewed is the likeliest next seed, and a thin
      // complementary set falls through to 'related' — warm that too so
      // the first open after "Add to cart" needs no network at all.
      if (pdpId) loadRecsHtml(recsUrlFor(base, section, pdpId, 'related')).catch(() => {});
    };
    if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 2500 });
    else setTimeout(run, 600);
  } catch {
    /* prewarm is best-effort */
  }
}

/** Maps a Shopify product type to a kit-builder slot key. */
function slotForType(type) {
  switch ((type || '').trim().toLowerCase()) {
    case 'rods':
      return 'rod';
    case 'reels':
      return 'reel';
    case 'line':
      return 'line';
    case 'lures':
      return 'lure';
    default:
      return 'more';
  }
}

class LpCartRecs extends HTMLElement {
  #listenerAbort = null;
  #fetchAbort = null;
  #renderSeq = 0;
  #cartIds = [];
  #cartTypes = [];
  #addInFlight = false;
  #observer = null;
  #stateVerified = false;
  #defaultHeading = null;
  #cartTotal = 0;

  /** True while the region is showing empty-cart merchandising. */
  #emptyMode = false;

  /** Card to restore focus to after a quick-add re-render (the add button is
      disabled in flight, which drops focus to <body> before #render runs). */
  #pendingFocusProductId = null;

  /** Earliest time the next refresh may repaint, so the 'Added' confirmation
      on a quick-add card is perceivable before the rail re-merges. */
  #holdUntil = 0;

  /** Monotonic cart-event ordering. Handlers fire in dispatch order but their
      deferred promises resolve in NETWORK order — after a removal, a slower
      earlier event can land last carrying the pre-removal cart and repaint
      stale cards over the empty-cart render. Stamp each event at dispatch,
      apply only if nothing newer has been applied. */
  #eventSeq = 0;

  #lastAppliedEventSeq = 0;

  /**
   * Theme-preview QA rides a preview_theme_id param/cookie. Cookie behaviour
   * for subresource fetches is not guaranteed in every context, so when the
   * page itself is a preview, every engine request carries the param too.
   * In production the param is absent and this appends nothing.
   */
  #withPreview(url) {
    try {
      const preview = new URLSearchParams(window.location.search).get('preview_theme_id');
      if (!preview) return url;
      return `${url}${url.includes('?') ? '&' : '?'}preview_theme_id=${encodeURIComponent(preview)}`;
    } catch {
      return url;
    }
  }

  connectedCallback() {
    try {
      // The drawer's empty-state template also carries a shell, while morph.js
      // preserves this element across cart swaps (data-skip-node-update) — so
      // a client-side transition to empty can leave TWO live shells in the
      // same dialog. Either one is self-sufficient; keep the first in DOM and
      // retire any later twin before it double-fetches or double-renders.
      const scope = this.closest('dialog') || document;
      const twins = [...scope.querySelectorAll('lp-cart-recs')].filter(
        (el) => el.dataset.context === this.dataset.context
      );
      if (twins.length > 1 && twins[0] !== this) {
        this.remove();
        return;
      }

      this.#cartIds = this.#parseList(this.dataset.cartIds).map(Number).filter(Boolean);
      this.#cartTypes = this.#parseList(this.dataset.cartTypes);
      this.#cartTotal = Number(this.dataset.cartTotal) || 0;

      this.#listenerAbort = new AbortController();
      const { signal } = this.#listenerAbort;

      document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartEvent, { signal });
      document.addEventListener(StandardEvents.cartDiscountUpdate, this.#onCartEvent, { signal });
      this.addEventListener('click', this.#onClick, { signal });
      this.addEventListener('change', this.#onChange, { signal });

      this.#observer = new IntersectionObserver(
        (entries, observer) => {
          if (!entries[0]?.isIntersecting) return;
          observer.disconnect();
          this.#refresh();
        },
        { rootMargin: '0px 0px 300px 0px' }
      );
      // A hidden element never intersects, so observe against the parent region
      // that will scroll it into view once populated; fall back to an immediate
      // deferred refresh when IO is unavailable.
      this.#observer.observe(this.parentElement ?? this);
    } catch (error) {
      this.#fail(error);
    }
  }

  disconnectedCallback() {
    this.#listenerAbort?.abort();
    this.#fetchAbort?.abort();
    this.#observer?.disconnect();
  }

  /** @param {string | undefined} value */
  #parseList(value) {
    return (value || '')
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  /** Extracts cart product ids/types/total from a resolved cart event, or null. */
  #stateFromEventResult(result) {
    const items = result?.detail?.items;
    if (Array.isArray(items)) {
      return {
        ids: [...new Set(items.map((item) => item.product_id).filter(Boolean))],
        types: [...new Set(items.map((item) => item.product_type).filter(Boolean))],
        total: items.reduce((sum, item) => sum + (item.final_line_price || 0), 0),
      };
    }
    return null;
  }

  #onCartEvent = (event) => {
    const eventSeq = ++this.#eventSeq;
    const promise = event.promise instanceof Promise ? event.promise : Promise.resolve(null);
    promise
      .then(async (result) => {
        let state = this.#stateFromEventResult(result);
        if (!state) state = await this.#fetchCartState();
        if (!state) return;
        // A dispatch-earlier event resolving after a dispatch-later one is
        // stale by definition — drop it before it can overwrite newer state.
        if (eventSeq < this.#lastAppliedEventSeq) return;
        this.#lastAppliedEventSeq = eventSeq;

        const changed =
          state.ids.length !== this.#cartIds.length || state.ids.some((id) => !this.#cartIds.includes(id));
        this.#cartIds = state.ids;
        this.#cartTypes = state.types;
        if (typeof state.total === 'number') this.#cartTotal = state.total;
        this.#stateVerified = true;

        if (this.#cartIds.length === 0) {
          if (this.#emptyConfigured()) {
            this.#refresh();
            return;
          }
          this.hidden = true;
          this.#body().replaceChildren();
          return;
        }
        if (changed || this.hidden) {
          const hold = this.#holdUntil - Date.now();
          this.#holdUntil = 0;
          if (hold > 0) setTimeout(() => this.#refresh(), hold);
          else this.#refresh();
        } else {
          // Quantity-only changes move the total, which moves the
          // free-shipping nudge math without changing the card set.
          this.#applyNudges();
        }
      })
      .catch(() => {
        /* mutation failed upstream; keep current render */
      });
  };

  async #fetchCartState() {
    try {
      const response = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return null;
      const cart = await response.json();
      const state = this.#stateFromEventResult({ detail: { items: cart.items } });
      if (state && typeof cart.total_price === 'number') state.total = cart.total_price;
      return state;
    } catch {
      return null;
    }
  }

  #body() {
    return this.querySelector('.lp-cart-recs__body') ?? this;
  }

  /** Empty-cart merchandising is on only when the shell provides a source. */
  #emptyConfigured() {
    return this.dataset.emptyEnabled === 'true' && !!this.dataset.emptyUrl;
  }

  /**
   * Paint-time truth. Event payloads and template-cloned data attributes can
   * both carry a stale cart (the empty-state template is captured while the
   * cart is still full, and a slow event can resolve out of order) — but by
   * the time a fetch has round-tripped, the section morph has already
   * rendered the REAL line items next to this shell. Whether the surface
   * shows any rows is therefore the one signal that cannot be stale, so it
   * arbitrates which mode may paint; #cartIds only chooses what to fetch.
   */
  #surfaceLooksEmpty() {
    const scope =
      this.closest('dialog') || this.closest('cart-items-component')?.parentElement || document;
    return !scope.querySelector('.cart-items__table-row');
  }

  /**
   * Empty-cart mode: the drawer's dead space shows the merchant's fallback
   * collection — or best sellers from /collections/all when none is set —
   * under its own heading. Same card pipeline, same fail-closed policy; the
   * free-shipping nudge and the "recommended for your cart" kicker are
   * suppressed because there is no cart to reason about.
   */
  async #refreshEmpty() {
    if (!this.#stateVerified) {
      const live = await this.#fetchCartState();
      if (live) {
        this.#cartIds = live.ids;
        this.#cartTypes = live.types;
      }
      this.#stateVerified = true;
      // The verify may reveal the cart is not empty after all.
      if (this.#cartIds.length > 0) return this.#refresh();
    }
    const seq = ++this.#renderSeq;
    this.#fetchAbort?.abort();
    this.#fetchAbort = new AbortController();
    const { signal } = this.#fetchAbort;
    const limit = Math.max(2, Math.min(8, Number(this.dataset.limit) || 4));

    try {
      const sep = this.dataset.emptyUrl.includes('?') ? '&' : '?';
      let url = `${this.dataset.emptyUrl}${sep}section_id=${encodeURIComponent(this.dataset.sourceSection || 'lp-cart-recs')}`;
      if (this.dataset.emptySort) url += `&sort_by=${encodeURIComponent(this.dataset.emptySort)}`;
      const cards = this.#merge([await this.#fetchCards(url, signal)], limit);

      if (seq !== this.#renderSeq) return;
      // Symmetric guard: if the surface shows line items — an add landed
      // while this fetch was in flight — hand back to the seeded path rather
      // than paint empty-mode merchandising over a cart that has items.
      if (!this.#surfaceLooksEmpty()) {
        this.#stateVerified = false;
        return this.#refresh();
      }
      if (cards.length === 0) {
        this.hidden = true;
        this.#body().replaceChildren();
        return;
      }

      this.#emptyMode = true;
      this.#setEmptyChrome(true);
      this.#render(cards);
      this.#applyNudges();
      this.hidden = false;
      this.#announce(this.dataset.msgUpdated || '');
    } catch (error) {
      if (error?.name === 'AbortError') return;
      if (seq === this.#renderSeq) this.#fail(error);
    }
  }

  /** Swaps the region chrome between cart-seeded and empty-cart modes. */
  #setEmptyChrome(on) {
    const kicker = this.querySelector('.lp-cart-recs__kicker');
    // Inline display, not [hidden]: the shell's stylesheet sets display on
    // the kicker, which out-cascades the hidden attribute's UA default.
    if (kicker) kicker.style.display = on ? 'none' : '';
    if (on) {
      const heading = this.querySelector('.lp-cart-recs__heading');
      if (heading) {
        if (this.#defaultHeading === null) this.#defaultHeading = heading.textContent;
        heading.textContent = this.dataset.emptyHeading || this.#defaultHeading;
      }
    }
  }

  #announce(message) {
    const status = this.querySelector('.lp-cart-recs__status');
    if (status) status.textContent = message;
  }

  async #refresh() {
    // The server-rendered data-cart-ids can lie in one real path: the
    // empty-cart template is captured while the cart is still full, and its
    // clone is swapped in when the last line is removed. Verify against
    // /cart.js once before the first render.
    // 2026-09-14: the verification no longer gates the first paint. Render
    // from the server-rendered ids immediately (usually straight from the
    // prewarmed cache) while /cart.js is checked in parallel; a different
    // answer triggers one corrective refresh.
    if (!this.#stateVerified) {
      this.#stateVerified = true;
      const before = this.#cartIds.join(',');
      this.#fetchCartState().then((live) => {
        if (!live) return;
        this.#cartTypes = live.types;
        if (typeof live.total === 'number') this.#cartTotal = live.total;
        const same = live.ids.join(',') === before;
        this.#cartIds = live.ids;
        if (!same) this.#refresh();
      });
    }

    if (this.#cartIds.length === 0) {
      if (this.#emptyConfigured()) return this.#refreshEmpty();
      this.hidden = true;
      return;
    }

    const seq = ++this.#renderSeq;
    this.#fetchAbort?.abort();
    this.#fetchAbort = new AbortController();
    const { signal } = this.#fetchAbort;
    const limit = Math.max(2, Math.min(8, Number(this.dataset.limit) || 4));

    try {
      const seeds = this.#cartIds.slice(0, SEED_LIMIT);
      const complementary = await Promise.all(
        seeds.map((seed) => this.#fetchCards(this.#recsUrl(seed, 'complementary'), signal))
      );

      let cards = this.#merge(complementary, limit);

      if (cards.length < limit) {
        const related = await Promise.all(
          seeds.slice(0, 2).map((seed) => this.#fetchCards(this.#recsUrl(seed, 'related'), signal))
        );
        cards = this.#merge([cards, ...related], limit);
      }

      if (cards.length === 0 && this.dataset.fallbackUrl) {
        const fallback = await this.#fetchCards(
          `${this.dataset.fallbackUrl}?section_id=${encodeURIComponent(this.dataset.sourceSection || 'lp-cart-recs')}`,
          signal
        );
        cards = this.#merge([fallback], limit);
      }

      if (seq !== this.#renderSeq) return;

      // The cart may have emptied while these fetches were in flight, and
      // this instance's own #cartIds may itself be the lie (stale event,
      // template-cloned attributes). The rendered surface cannot be stale at
      // this point — arbitrate on it, not on our beliefs.
      if (this.#surfaceLooksEmpty()) {
        // Our ids said items, the surface says none — our state is the lie.
        // Force a live re-verify on the way through so the contradiction is
        // corrected once rather than bounced between the two paths.
        this.#stateVerified = false;
        if (this.#emptyConfigured()) return this.#refreshEmpty();
        this.hidden = true;
        this.#body().replaceChildren();
        return;
      }

      if (cards.length === 0) {
        this.hidden = true;
        this.#body().replaceChildren();
        return;
      }

      this.#emptyMode = false;
      this.#setEmptyChrome(false);
      this.#applyHeading(cards);
      this.#render(cards);
      this.#applyNudges();
      this.hidden = false;
      this.#announce(this.dataset.msgUpdated || '');
    } catch (error) {
      if (error?.name === 'AbortError') return;
      if (seq === this.#renderSeq) this.#fail(error);
    }
  }

  #recsUrl(productId, intent) {
    return recsUrlFor(this.dataset.recsUrl, this.dataset.sourceSection, productId, intent);
  }

  /**
   * Fetches server-rendered recommendation cards for a URL.
   * @returns {Promise<Element[]>}
   */
  async #fetchCards(url, signal) {
    let html;
    try {
      html = await loadRecsHtml(url);
    } catch (error) {
      if (error?.name === 'AbortError') throw error;
      return [];
    }
    if (signal?.aborted) throw Object.assign(new Error('aborted'), { name: 'AbortError' });
    if (!html) return [];
    return this.#parseCards(html);
  }

  #parseCards(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return [...doc.querySelectorAll('[data-lp-rec-card]')];
  }

  /**
   * Interleaves card lists, drops in-cart products and duplicates, caps at limit.
   * @param {Element[][]} lists
   */
  #merge(lists, limit) {
    const seen = new Set(this.#cartIds.map(String));
    const merged = [];
    const max = Math.max(0, ...lists.map((list) => list.length));

    for (let index = 0; index < max && merged.length < limit; index += 1) {
      for (const list of lists) {
        if (merged.length >= limit) break;
        const card = list[index];
        if (!card) continue;
        const id = card.getAttribute('data-product-id');
        if (!id || seen.has(id)) continue;
        seen.add(id);
        merged.push(card);
      }
    }
    return merged;
  }

  /**
   * Honest heading: when every surviving card came from the merchant's
   * fallback collection (no complementary/related data), "Complete your
   * setup" would overclaim — use the collection's real title instead.
   */
  #applyHeading(cards) {
    const heading = this.querySelector('.lp-cart-recs__heading');
    if (!heading) return;
    if (this.#defaultHeading === null) this.#defaultHeading = heading.textContent;

    const allFallback = cards.every((card) => card.getAttribute('data-source') === 'fallback');
    const fallbackHeading = this.dataset.fallbackHeading;
    heading.textContent = allFallback && fallbackHeading ? fallbackHeading : this.#defaultHeading;
  }

  /**
   * "Unlocks free shipping" nudge — real-offer math only: requires the
   * merchant-set threshold AND that adding this card's selected variant
   * lifts the live cart total across it. No threshold, no badge.
   */
  #applyNudges() {
    if (this.#emptyMode) {
      for (const badge of this.querySelectorAll('.lp-cart-recs__nudge')) badge.remove();
      return;
    }
    const threshold = Number(this.dataset.thresholdCents) || 0;
    const label = this.dataset.labelUnlocks || '';
    if (!label) return;
    const remaining = threshold - this.#cartTotal;

    // `price >= remaining` alone badges EVERY card that clears the gap, so a
    // 649,00 reel gets tagged as the way to close a 44,09 shortfall while the
    // 41,95 lure beside it does not — and once the gap is small enough, every
    // card carries it and the nudge means nothing. Badge only the cheapest
    // card that actually closes the gap: one badge, and always the one worth
    // acting on.
    const cards = [...this.querySelectorAll('[data-lp-rec-card]')];
    const priceOf = (card) => {
      const select = card.querySelector('[data-lp-rec-variant-select]');
      return select
        ? Number(select.selectedOptions[0]?.dataset.priceCents) || 0
        : Number(card.getAttribute('data-price-cents')) || 0;
    };

    let winner = null;
    if (threshold > 0 && remaining > 0) {
      for (const card of cards) {
        const price = priceOf(card);
        if (price >= remaining && (winner === null || price < priceOf(winner))) winner = card;
      }
    }

    for (const card of cards) {
      const earned = card === winner;
      let badge = card.querySelector('.lp-cart-recs__nudge');
      if (earned && !badge) {
        badge = document.createElement('span');
        badge.className = 'lp-cart-recs__nudge';
        badge.textContent = label;
        card.querySelector('.lp-cart-recs__info')?.append(badge);
      } else if (!earned && badge) {
        badge.remove();
      }
    }
  }

  #render(cards) {
    const body = this.#body();
    const style = this.dataset.style || 'tackle-desk';

    // Re-rendering destroys the previously focused control; remember which
    // product's action had focus so keyboard users keep their place.
    const active = document.activeElement;
    const focusCard = this.contains(active) ? active.closest('[data-lp-rec-card]') : null;
    const focusProductId = focusCard?.getAttribute('data-product-id') ?? this.#pendingFocusProductId;
    this.#pendingFocusProductId = null;
    const focusSelector = active?.matches?.('[data-lp-rec-variant-select]')
      ? '[data-lp-rec-variant-select]'
      : '[data-lp-rec-add], .lp-cart-recs__choose';

    if (style === 'kit-builder') {
      body.replaceChildren(...this.#renderSlots(cards));
    } else {
      const rail = document.createElement('ul');
      rail.className = 'lp-cart-recs__rail';
      rail.setAttribute('role', 'list');
      for (const card of cards) rail.append(document.importNode(card, true));
      body.replaceChildren(rail);
    }

    if (focusProductId) {
      const target =
        body.querySelector(`[data-product-id="${focusProductId}"]`)?.querySelector(focusSelector) ??
        this.querySelector('.lp-cart-recs__heading');
      if (target) {
        if (!target.matches('button, a, select, [tabindex]')) target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      }
    }
  }

  /** Groups cards into kit slots; missing gear categories lead. */
  #renderSlots(cards) {
    const cartSlots = new Set(this.#cartTypes.map(slotForType));
    const groups = new Map();
    for (const card of cards) {
      const slot = slotForType(card.getAttribute('data-product-type'));
      if (!groups.has(slot)) groups.set(slot, []);
      groups.get(slot).push(card);
    }

    const order = [
      ...GEAR_SLOTS.filter((slot) => !cartSlots.has(slot)),
      ...GEAR_SLOTS.filter((slot) => cartSlots.has(slot)),
      'lure',
      'more',
    ];

    const headingTag = this.dataset.context === 'drawer' ? 'h4' : 'h3';
    const fragments = [];
    for (const slot of order) {
      const slotCards = groups.get(slot);
      if (!slotCards?.length) continue;

      const section = document.createElement('div');
      section.className = 'lp-cart-recs__slot';

      const label = document.createElement(headingTag);
      label.className = 'lp-cart-recs__slot-label';
      label.textContent = this.dataset[`label${slot[0].toUpperCase()}${slot.slice(1)}`] || '';

      const rail = document.createElement('ul');
      rail.className = 'lp-cart-recs__rail';
      rail.setAttribute('role', 'list');
      for (const card of slotCards) rail.append(document.importNode(card, true));

      if (label.textContent) section.append(label);
      section.append(rail);
      fragments.push(section);
    }
    return fragments;
  }

  #onChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.matches('[data-lp-rec-variant-select]')) return;

    const card = select.closest('[data-lp-rec-card]');
    const button = card?.querySelector('[data-lp-rec-add]');
    const option = select.selectedOptions[0];
    if (!button || !option) return;

    button.dataset.variantId = option.value;
    const price = card.querySelector('[data-lp-rec-price]');
    if (price && option.dataset.price) price.textContent = option.dataset.price;

    // Compare-at price is per-variant: update or remove the strike-through so
    // a non-discounted variant never inherits another variant's deal styling.
    let compare = card.querySelector('.lp-cart-recs__price-compare');
    if (option.dataset.compare) {
      if (!compare) {
        compare = document.createElement('s');
        compare.className = 'lp-cart-recs__price-compare';
        price?.parentElement?.prepend(compare);
      }
      compare.textContent = option.dataset.compare;
      price?.classList.add('lp-cart-recs__price-current--deal');
    } else {
      compare?.remove();
      price?.classList.remove('lp-cart-recs__price-current--deal');
    }

    this.#applyNudges();
  };

  #onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-lp-rec-add]') : null;
    if (!button || !this.contains(button)) return;
    event.preventDefault();
    this.#quickAdd(button);
  };

  /**
   * Adds one variant through the theme's cart event contract: dispatch
   * CartLinesUpdateEvent before the request with a deferred promise, then
   * resolve it with the refreshed cart + bundled section HTML so
   * cart-items-component morphs every cart surface. Mutations are serialized —
   * Shopify's cart API returns 409 for concurrent writes.
   */
  async #quickAdd(button) {
    if (this.#addInFlight) return;
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    this.#addInFlight = true;
    button.setAttribute('aria-busy', 'true');
    if (document.activeElement === button || button.contains(document.activeElement)) {
      this.#pendingFocusProductId =
        button.closest('[data-lp-rec-card]')?.getAttribute('data-product-id') || null;
    }
    button.disabled = true;
    button.closest('[data-lp-rec-card]')?.classList.remove('lp-cart-recs__card--error');

    const title = button.dataset.productTitle || '';
    const sectionIds = [
      ...new Set(
        [...document.querySelectorAll('cart-items-component')]
          .map((component) => component.dataset.sectionId)
          .filter(Boolean)
      ),
    ].slice(0, SECTIONS_PARAM_MAX);

    const formData = new FormData();
    formData.set('id', variantId);
    formData.set('quantity', '1');
    if (sectionIds.length) {
      formData.set('sections', sectionIds.join(','));
      formData.set('sections_url', window.location.pathname);
    }

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add', {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
        body: formData,
      });
      const payload = await response.json().catch(() => ({}));

      if (!response.ok || payload.status) {
        throw new Error(payload.message || `Add to cart failed (${response.status})`);
      }

      const cartResponse = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`, {
        headers: { Accept: 'application/json' },
      });
      const ajaxCart = await cartResponse.json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'lp-cart-recs',
          sourceId: this.id || 'lp-cart-recs',
          itemCount: 1,
          sections: payload.sections,
          didError: false,
        },
      });

      const addLabel = button.querySelector('.lp-cart-recs__add-label');
      if (addLabel && this.dataset.labelAdded) addLabel.textContent = this.dataset.labelAdded;
      this.#announce((this.dataset.msgAdded || '[TITLE]').replace('[TITLE]', title));
      this.#holdUntil = Date.now() + 1800;
    } catch (error) {
      deferred.reject(error);
      button.closest('[data-lp-rec-card]')?.classList.add('lp-cart-recs__card--error');
      this.#announce((this.dataset.msgFailed || '[TITLE]').replace('[TITLE]', title));
    } finally {
      this.#addInFlight = false;
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
  }

  #fail(error) {
    this.hidden = true;
    console.error('[lp-cart-recs]', error);
  }
}

if (!customElements.get('lp-cart-recs')) {
  customElements.define('lp-cart-recs', LpCartRecs);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', prewarmRecs, { once: true });
} else {
  prewarmRecs();
}
